<script setup lang="ts">
import { ref, computed, watch, type ComponentPublicInstance } from "vue";
import { Modal } from "bootstrap";
import { useChangePassword } from "@/queries/auth";
import { useAlertStore } from "@/stores/alerts-store";

const props = defineProps<{
  show: boolean;
}>();

const emit = defineEmits<{
  (e: "update:show", value: boolean): void;
}>();

const alertStore = useAlertStore();
const { changePassword, changePasswordPending: isSubmitting, changePasswordError, changePasswordReset } =
  useChangePassword();

const modalRef = ref<HTMLElement | null>(null);
const modalInstance = ref<Modal | null>(null);

const currentPassword = ref("");
const newPassword = ref("");
const confirmPassword = ref("");

// Entra External ID default password policy for local accounts: 8–256 chars and at least
// 3 of 4 character classes (lowercase, uppercase, digit, symbol). Mirror it client-side so
// users get instant feedback instead of a failed round-trip.
const charClassesMet = computed(() => {
  const p = newPassword.value;
  return [/[a-z]/, /[A-Z]/, /[0-9]/, /[^a-zA-Z0-9]/].filter((re) => re.test(p)).length;
});
const meetsLength = computed(() => newPassword.value.length >= 8);
const meetsComplexity = computed(() => charClassesMet.value >= 3);
const passwordsMatch = computed(() => newPassword.value === confirmPassword.value);
const newIsDifferent = computed(() => newPassword.value !== currentPassword.value);

const newPasswordValid = computed(() => meetsLength.value && meetsComplexity.value && newIsDifferent.value);
const canSubmit = computed(
  () =>
    currentPassword.value.length > 0 &&
    newPasswordValid.value &&
    passwordsMatch.value &&
    !isSubmitting.value
);

const serverError = computed(() => changePasswordError.value?.userMessage ?? "");

function resetForm() {
  currentPassword.value = "";
  newPassword.value = "";
  confirmPassword.value = "";
  changePasswordReset();
}

watch(
  () => props.show,
  (show) => {
    if (show) {
      resetForm();
      modalInstance.value?.show();
    } else {
      modalInstance.value?.hide();
    }
  }
);

function handleSubmit() {
  if (!canSubmit.value) return;
  changePassword(
    { currentPassword: currentPassword.value, newPassword: newPassword.value },
    {
      onSuccess: () => {
        alertStore.addSuccess("Password changed", "Your password has been updated successfully.");
        closeModal();
      },
    }
  );
}

function closeModal() {
  emit("update:show", false);
}

function onModalHidden() {
  emit("update:show", false);
}

const initModal = (el: Element | ComponentPublicInstance | null) => {
  if (el && el instanceof HTMLElement && !modalInstance.value) {
    modalRef.value = el;
    modalInstance.value = new Modal(el);
    el.addEventListener("hidden.bs.modal", onModalHidden);
  }
};
</script>

<template>
  <div
    :ref="initModal"
    class="modal fade"
    tabindex="-1"
    aria-labelledby="changePasswordModalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="changePasswordModalLabel">Change Password</h5>
          <button type="button" class="btn-close" @click="closeModal" aria-label="Close"></button>
        </div>
        <form @submit.prevent="handleSubmit">
          <div class="modal-body">
            <div v-if="serverError" class="alert alert-danger" role="alert">
              {{ serverError }}
            </div>

            <div class="mb-3">
              <label for="currentPassword" class="form-label">Current password</label>
              <input
                id="currentPassword"
                type="password"
                class="form-control"
                v-model="currentPassword"
                autocomplete="current-password"
                required
                :disabled="isSubmitting"
              />
            </div>

            <div class="mb-3">
              <label for="newPassword" class="form-label">New password</label>
              <input
                id="newPassword"
                type="password"
                class="form-control"
                :class="{ 'is-invalid': newPassword.length > 0 && !newPasswordValid }"
                v-model="newPassword"
                autocomplete="new-password"
                required
                :disabled="isSubmitting"
              />
              <div v-if="newPassword.length > 0 && !newIsDifferent" class="invalid-feedback">
                New password must be different from your current password.
              </div>
              <div class="form-text">
                At least 8 characters and 3 of: lowercase, uppercase, number, symbol.
              </div>
            </div>

            <div class="mb-3">
              <label for="confirmPassword" class="form-label">Confirm new password</label>
              <input
                id="confirmPassword"
                type="password"
                class="form-control"
                :class="{ 'is-invalid': confirmPassword.length > 0 && !passwordsMatch }"
                v-model="confirmPassword"
                autocomplete="new-password"
                required
                :disabled="isSubmitting"
              />
              <div v-if="confirmPassword.length > 0 && !passwordsMatch" class="invalid-feedback">
                Passwords do not match.
              </div>
            </div>
          </div>

          <div class="modal-footer">
            <button type="button" class="bb-btn-cancel" @click="closeModal" :disabled="isSubmitting">
              Cancel
            </button>
            <button type="submit" class="bb-btn-primary-cta" :disabled="!canSubmit">
              <span
                v-if="isSubmitting"
                class="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              {{ isSubmitting ? "Changing…" : "Change password" }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>
